import React, { ReactNode, useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { faPlus, faSpinner, faTrash } from '@fortawesome/free-solid-svg-icons'
import Button, { ButtonSize, ButtonTheme } from 'components/Form/Button'
import Card from 'components/Layout/Card'
import FormRow from 'components/Form/FormRow'
import Input from 'components/Form/Input'
import DatePickerSingle from 'components/Form/DatePickerSingle'
import FillingSpinner from 'components/FillingSpinner'
import PageHeader from 'components/Layout/PageHeader'
import Table, { TableBody, TableData, TableRow } from 'components/Table'
import { ReactRouterParams } from 'types/various'
import { DATE_PLACEHOLDER } from 'constant'
import { AppContext } from '../../../contexts/AppContext'
import { Routes } from '../../../routes'
import { RootState } from '../../../store'
import service from '../service'
import { priceListFormActions } from './store/price-list-form-slice'
import {
  columnLabel,
  columnType,
  END_DATE_COLUMN,
  formatValueColumn,
  getInvalidPeriodRows,
  isDateColumn,
  isDefaultPeriodRow,
  isFloatInput,
  isPastPeriodRow,
  isPeriodDayDisabled,
  isValidDateCell,
  isValidValueCell,
  PriceListColumnType,
  PriceListGridKey,
  PricingGrid,
  START_DATE_COLUMN,
} from '../models/price-list-form-slice.model'
import './PriceListForm.scss'

const PriceListForm: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { addNotification } = useContext(AppContext)
  const { id: urlId } = useParams<ReactRouterParams>()

  const { name, pricing, loading } = useSelector((state: RootState) => state.priceListForm)

  useEffect(() => {
    if (urlId) {
      service.getPriceList(urlId)
    } else {
      service.loadTemplate()
    }
  }, [urlId])

  const handleSubmit = (): void => {
    service.handleSubmit(navigate, addNotification, t, urlId)
  }

  // Translated label for the shared date column headers.
  const dateHeaderLabel = (header: string): string => {
    switch ((header ?? '').toLowerCase()) {
      case START_DATE_COLUMN:
        return t('priceLists.fromDate')
      case END_DATE_COLUMN:
        return t('priceLists.endDate')
      default:
        return header
    }
  }

  // A value cell starts a new media_format/offer group when the previous column
  // is a date column or belongs to a different group. Used to draw a divider
  // between the per-column groups.
  const isValueGroupStart = (headers: string[], col: number): boolean => {
    if (isDateColumn(headers[col]) || columnType(headers[col]) === null) {
      return false
    }
    const previous = headers[col - 1]
    return (
      !previous || isDateColumn(previous) || columnLabel(previous) !== columnLabel(headers[col])
    )
  }

  // Groups the value columns by media_format/offer, keeping each group's typed
  // cells (impressions, audience) together and in header order.
  const valueColumnGroups = (
    headers: string[]
  ): {
    label: string
    displayLabel: string
    cells: { header: string; index: number; type: PriceListColumnType }[]
  }[] => {
    const groups: {
      label: string
      displayLabel: string
      cells: { header: string; index: number; type: PriceListColumnType }[]
    }[] = []

    headers.forEach((header, index) => {
      const type = columnType(header)
      if (isDateColumn(header) || type === null) {
        return
      }
      const label = columnLabel(header)
      const group = groups.find(existing => existing.label === label)
      const cell = { header, index, type }
      if (group) {
        group.cells.push(cell)
      } else {
        groups.push({ label, displayLabel: formatValueColumn(label), cells: [cell] })
      }
    })

    return groups
  }

  const GridTable = (gridKey: PriceListGridKey, grid: PricingGrid, label: string): ReactNode => {
    const invalidPeriodRows = getInvalidPeriodRows(grid)
    const firstDateCol = grid.headers.findIndex(isDateColumn)
    const dateHeaders = grid.headers
      .map((header, index) => ({ header, index }))
      .filter(({ header }) => isDateColumn(header))
    const valueGroups = valueColumnGroups(grid.headers)

    return (
      <>
        <p className='Pricing_Report__report-type'>
          {grid.headers.length ? label : t('priceLists.noColumns', { name: label })}
        </p>
        {!!grid.headers.length && (
          <>
            <div className='PriceListForm__table-scroll'>
              <Table>
                <thead className='TableHeader'>
                  <tr className='TableHeader__row'>
                    <th
                      rowSpan={2}
                      className='TableHeaderItem Pricing_Report__header PriceListForm__sticky-actions'
                    />
                    {dateHeaders.map(({ header, index }, dateIndex) => (
                      <th
                        key={index}
                        rowSpan={2}
                        className={`TableHeaderItem Pricing_Report__header PriceListForm__sticky-col PriceListForm__sticky-col--${
                          dateIndex === 0 ? 'first' : 'second'
                        }`}
                      >
                        {dateHeaderLabel(header)}
                      </th>
                    ))}
                    {valueGroups.map(group => (
                      <th
                        key={group.label}
                        colSpan={group.cells.length}
                        className='TableHeaderItem Pricing_Report__header PriceListForm__group-header'
                      >
                        {group.displayLabel}
                      </th>
                    ))}
                  </tr>
                  <tr className='TableHeader__row'>
                    {valueGroups.flatMap(group =>
                      group.cells.map((valueCell, cellIndex) => (
                        <th
                          key={valueCell.index}
                          className={`TableHeaderItem Pricing_Report__header PriceListForm__subheader${
                            cellIndex === 0 ? ' PriceListForm__cell--group-start' : ''
                          }`}
                        >
                          {t(`priceLists.${valueCell.type}`)}
                        </th>
                      ))
                    )}
                  </tr>
                </thead>
                <TableBody>
                  {grid.content.map((row, rowIndex) => {
                    const isDefaultRow = isDefaultPeriodRow(rowIndex)
                    const isPastRow = isPastPeriodRow(grid, rowIndex)

                    return (
                      <TableRow
                        key={rowIndex}
                        className={`Pricing_Report__row${
                          isPastRow ? ' PriceListForm__row--past' : ''
                        }${isDefaultRow ? ' PriceListForm__row--default' : ''}`}
                      >
                        {[
                          <TableData
                            key='actions'
                            className='PriceListForm__cell-actions PriceListForm__sticky-actions'
                          >
                            {!isDefaultRow && (
                              <Button
                                theme={ButtonTheme.RED_OUTLINE}
                                size={ButtonSize.SMALL}
                                icon={faTrash}
                                onClick={() =>
                                  void dispatch(
                                    priceListFormActions.removeRow({ grid: gridKey, row: rowIndex })
                                  )
                                }
                              />
                            )}
                          </TableData>,
                          ...row
                            .map((cell, col) => {
                              if (isDateColumn(grid.headers[col])) {
                                if (isDefaultRow) {
                                  // The default-period label spans all the date
                                  // columns so it isn't clipped by a single fixed
                                  // sticky column width.
                                  if (col !== firstDateCol) {
                                    return null
                                  }
                                  return (
                                    <td
                                      key={col}
                                      colSpan={dateHeaders.length}
                                      className='TableData PriceListForm__cell-default PriceListForm__sticky-col PriceListForm__cell-default--full'
                                    >
                                      {t('priceLists.defaultPeriod')}
                                    </td>
                                  )
                                }

                                return (
                                  <TableData
                                    key={col}
                                    className={`PriceListForm__sticky-col PriceListForm__sticky-col--${
                                      col === firstDateCol ? 'first' : 'second'
                                    }`}
                                  >
                                    <DatePickerSingle
                                      id={`${gridKey}-${rowIndex}-${col}`}
                                      className={`PriceListForm__cell-date${
                                        isValidDateCell(cell) && !invalidPeriodRows.has(rowIndex)
                                          ? ''
                                          : ' PriceListForm__cell-date--error'
                                      }`}
                                      placeholder={DATE_PLACEHOLDER}
                                      appendToBody={true}
                                      disabled={isPastRow}
                                      isOutsideRange={day =>
                                        isPeriodDayDisabled(grid, rowIndex, col, day)
                                      }
                                      value={cell}
                                      onChange={value =>
                                        void dispatch(
                                          priceListFormActions.setCell({
                                            grid: gridKey,
                                            row: rowIndex,
                                            col,
                                            value,
                                          })
                                        )
                                      }
                                    />
                                  </TableData>
                                )
                              }

                              return (
                                <TableData
                                  key={col}
                                  className={
                                    isValueGroupStart(grid.headers, col)
                                      ? 'PriceListForm__cell--group-start'
                                      : ''
                                  }
                                >
                                  <input
                                    className={`PriceListForm__cell-input${
                                      isValidValueCell(cell)
                                        ? ''
                                        : ' PriceListForm__cell-input--error'
                                    }`}
                                    value={cell}
                                    disabled={isPastRow}
                                    onChange={e => {
                                      if (!isFloatInput(e.target.value)) {
                                        return
                                      }
                                      void dispatch(
                                        priceListFormActions.setCell({
                                          grid: gridKey,
                                          row: rowIndex,
                                          col,
                                          value: e.target.value,
                                        })
                                      )
                                    }}
                                  />
                                </TableData>
                              )
                            })
                            .filter((cell): cell is JSX.Element => cell !== null),
                        ]}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>

            <Button
              className='PriceListForm__add-period'
              theme={ButtonTheme.BLUE_OUTLINE}
              size={ButtonSize.SMALL}
              icon={faPlus}
              onClick={() => void dispatch(priceListFormActions.addRow({ grid: gridKey }))}
            >
              {t('priceLists.addPeriod')}
            </Button>
          </>
        )}
      </>
    )
  }

  return (
    <>
      <PageHeader>
        {urlId ? t('priceLists.form.editPriceList') : t('priceLists.form.newPriceList')}
      </PageHeader>

      <Card>
        {loading ? (
          <FillingSpinner className='PriceListForm__loading' />
        ) : (
          <div className='PriceListForm__container'>
            <FormRow>
              <Input
                id='name'
                title={t('common.name')}
                value={name}
                placeholder={t('common.name')}
                onChange={val => void dispatch(priceListFormActions.changeName(val))}
              />
            </FormRow>

            <p className='Pricing_Report__info'>{t('report.priceReportInfo')}</p>

            {GridTable('pricing', pricing, t('priceLists.pricing'))}
          </div>
        )}

        <div className='PriceListForm__buttons'>
          <Button
            icon={loading ? faSpinner : undefined}
            disabled={loading}
            onClick={handleSubmit}
          >
            {t('common.save')}
          </Button>
          <Button
            theme={ButtonTheme.BLUE_OUTLINE}
            toUrl={Routes.PRICE_LISTS.ALL}
          >
            {t('common.cancel')}
          </Button>
        </div>
      </Card>
    </>
  )
}

export default PriceListForm
